import * as React from "react";
import { Button } from "@/components/ui";
import {
  alreadyAttemptedChunkRecovery,
  errorMessage,
  isChunkLoadError,
  recoverFromStaleAssets
} from "@/lib/pwaRecovery";

type Props = {
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
  error: unknown;
};

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, error };
  }

  componentDidCatch(err: unknown) {
    console.error("Render error:", err);
    // Auto-recover once on lazy-chunk / deploy skew (#5)
    if (isChunkLoadError(err) && !alreadyAttemptedChunkRecovery()) {
      void recoverFromStaleAssets();
    }
  }

  render() {
    if (this.state.hasError) {
      const msg = errorMessage(this.state.error);
      const chunk = isChunkLoadError(this.state.error);
      return (
        <div className="min-h-[70vh] px-4 py-10" data-testid="error-boundary">
          <div className="mx-auto max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
            <div className="text-lg font-semibold text-slate-900 dark:text-slate-50">Hiba történt.</div>
            <div className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              {chunk
                ? "Az alkalmazás egy része nem töltődött be (elavult vagy hiányzó fájl — gyakran frissítés után). Próbáld újratölteni."
                : "Valami váratlanul félrement a felület megjelenítése közben. Próbáld újraindítani az oldalt."}
            </div>
            <p
              className="mt-3 break-words rounded-md bg-slate-50 p-2 font-mono text-xs text-slate-700 dark:bg-slate-900 dark:text-slate-300"
              data-testid="error-boundary-message"
            >
              {msg}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                data-testid="error-boundary-reload"
                onClick={() => {
                  if (chunk) void recoverFromStaleAssets();
                  else window.location.reload();
                }}
              >
                {chunk ? "Frissítés és újratöltés" : "Újraindítás"}
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
